import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import { NotificationsCanaryConfig } from 'src/app/models/notifications-canary.model';
import { Patient } from 'src/app/models/patient.model';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { FirestoreService } from 'src/app/services/firestore.service';

/**
 * Decides whether this user sees the notification bell and its panel.
 *
 * Reads {@link NotificationsCanaryConfig} from `welby_configuration/featureToggles` in real time and
 * cross-references the authenticated user's email, so the pilot group can be changed — or the panel
 * pulled — from Firestore without a deploy. Mirrors {@link VoicemailDropCanaryService}.
 *
 * Fails closed, and here that means **the app as it was before the panel existed**: no bell, no
 * panel. A missing, disabled or malformed config costs a nurse a convenience, never correctness —
 * the discharge is still in `patient_discharges` and the rows keep being written for when the group
 * opens up.
 *
 * Keyed on `user$`, so a logout resets the evaluation and a shared workstation never inherits the
 * previous user's group — and so the answer waits for the user document rather than for a flag that
 * fires before it. See the comment on `isEnabled$` for why the config read hangs off the user
 * instead of running beside it.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationsCanaryService {
  private static readonly CONFIG_PATH = 'welby_configuration/featureToggles';
  private static readonly FEATURE_KEY = 'notificationsCanary';

  /*
    The user comes FIRST and the config hangs off it. Both halves of that matter, and each was a
    separate cause of the same reported bug: "the bell only appears after a refresh".

    The user side is `user$`, not `isLoggedObservable$` plus a read of `auth.user`. The other canary
    services use that second shape and it races: `check()` calls `isLoggedSubject.next(true)` and
    only then `signInUsingToken(...)`, which is what populates `auth.user`. The subject fires while
    the field is still undefined. `user$` emits the user document itself, so there is no window in
    which a subscriber can observe "logged in" and "no user" at once — and it emits `null` on
    sign-out, which is what stops a shared workstation inheriting the previous nurse's group.

    The config is read only once there IS a user, because `welby_configuration/{document}` requires
    `request.auth != null`. Read at app start it fails with permission-denied, and the previous
    shape ran that read alongside the user in a `combineLatest` — so the failure resolved to `null`,
    `of(null)` completed, and the config half was dead for the rest of the session no matter who
    logged in afterwards.

    Hanging it off the user fixes both: no doomed pre-auth listener, and a fresh one per login.
  */
  readonly isEnabled$: Observable<boolean> = this.auth.user$.pipe(
    switchMap((user) =>
      user
        ? this.firestore.doc$(NotificationsCanaryService.CONFIG_PATH).pipe(
            // INSIDE the switchMap. `of(null)` completes, so a `catchError` on a source shared by
            // `combineLatest` would leave the config permanently null and the feature permanently
            // off — which is exactly what happened. Here a failed read costs this login only, and
            // the next one starts a fresh listener.
            catchError(() => of(null)),
            map((config) => this.evaluate(config?.[NotificationsCanaryService.FEATURE_KEY], user))
          )
        : of(false)
    ),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  constructor(private firestore: FirestoreService, private auth: NewAuthService) {}

  /**
   * True when this user may see the panel. Requires `enabled` plus an authenticated user; then only
   * `allowAll: true` (explicit opt-in) reaches everyone. An empty/omitted allowlist — or one made up
   * solely of blank entries — resolves to nobody, so flipping `enabled` before the group is populated
   * changes nothing for anyone. Otherwise the email must be listed.
   */
  private evaluate(config: NotificationsCanaryConfig | undefined, user: Patient | undefined): boolean {
    try {
      if (!config?.enabled || !user) {
        return false;
      }
      if (config.allowAll) {
        return true;
      }
      // Non-string and blank entries are dropped up front so a hand-edited Firestore doc (a number in
      // `emails`, a stray '') can neither throw nor match, and the length reflects real recipients.
      const emails = (config.emails ?? [])
        .filter((allowed) => typeof allowed === 'string')
        .map((allowed) => allowed.trim().toLowerCase())
        .filter((allowed) => !!allowed);
      if (emails.length === 0 || !user.email) {
        return false;
      }
      return emails.includes(user.email.trim().toLowerCase());
    } catch {
      // Any unexpected/malformed config can do no more than hide the bell, never error the
      // `isEnabled$` stream that the shell's template is subscribed to.
      return false;
    }
  }
}
